/* House & Co. 3.0 — cart + free refill-bottle engine, drawers, menu, product form, reveals. No dependencies. */
(() => {
  'use strict';
  const HC = window.HC || {};
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => [...r.querySelectorAll(s)];
  const root = (HC.routes && HC.routes.root) || '/';
  const url = (p) => root.replace(/\/$/, '') + p;
  const T = (k) => (HC.strings && HC.strings[k]) || k;
  const RM = matchMedia('(prefers-reduced-motion: reduce)').matches;

  HC.money = (cents) => {
    const fmt = HC.moneyFormat || 'LE {{amount}}';
    const v = cents / 100;
    const amount = v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => (k === 'amount_no_decimals' ? Math.round(v).toLocaleString('en-US') : amount));
  };

  let toastT;
  HC.toast = (msg) => {
    let el = $('#hc-toast');
    if (!el) { el = document.createElement('div'); el.id = 'hc-toast'; el.className = 'toast'; el.setAttribute('role', 'status'); el.setAttribute('aria-live', 'polite'); document.body.appendChild(el); }
    el.textContent = msg; el.classList.add('is-on');
    clearTimeout(toastT); toastT = setTimeout(() => el.classList.remove('is-on'), 3200);
  };

  /* ---------- cart API: serialised, every mutation followed by a free-bottle reconcile ---------- */
  const nativeFetch = window.fetch.bind(window);
  let internal = 0;
  const api = async (path, body) => {
    internal++;
    try {
      const res = await nativeFetch(url(path), body === undefined
        ? { headers: { Accept: 'application/json' }, credentials: 'same-origin' }
        : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, credentials: 'same-origin', body: JSON.stringify(body) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { const e = new Error(data.description || data.message || T('cartError')); e.status = res.status; throw e; }
      return data;
    } finally { internal--; }
  };
  const getCart = () => api('/cart.js');

  /* One free refill bottle per concentrate. Each gift group = an EGP 0 product + the concentrates it comes with.
     Gift lines are separate products, so bottles a customer chooses to buy are never touched. */
  const groups = ((HC.gift && HC.gift.groups) || []).filter(g => g.variant && g.handles && g.handles.length);
  const giftIds = new Set(groups.map(g => Number(g.variant)));
  HC.isGift = (i) => giftIds.has(Number(i.variant_id));
  const isKitLine = (i) => /bundle|kit/i.test(i.product_type || '') || Object.keys(i.properties || {}).some(k => /bundl/i.test(k));
  const kitContents = (i) => (HC.kits && HC.kits[i.handle]) || null;   // [{h, q}] for a kit line, else null

  const plan = (cart) => {
    const updates = {}; const adds = [];
    for (const g of groups) {
      const need = cart.items.filter(i => !HC.isGift(i)).reduce((n, i) => {
        const kc = kitContents(i);
        if (kc) return n + kc.filter(c => g.handles.includes(c.h)).reduce((a, c) => a + c.q, 0) * i.quantity;   // a kit brings a bottle per concentrate inside
        return isKitLine(i) ? n : n + (g.handles.includes(i.handle) ? i.quantity : 0);
      }, 0);
      const lines = cart.items.filter(i => Number(i.variant_id) === Number(g.variant));
      if (!lines.length) { if (need > 0) adds.push({ id: Number(g.variant), quantity: need, properties: { _hc_gift: g.key } }); continue; }
      lines.forEach((l, idx) => { const want = idx === 0 ? need : 0; if (l.quantity !== want) updates[l.key] = want; });
    }
    return { updates, adds };
  };

  let changed = false;
  const reconcile = async (cart) => {
    changed = false;
    cart = cart || await getCart();
    if (!groups.length) return cart;
    for (let pass = 0; pass < 3; pass++) {
      const { updates, adds } = plan(cart);
      if (!Object.keys(updates).length && !adds.length) return cart;
      changed = true;
      if (Object.keys(updates).length) cart = await api('/cart/update.js', { updates });
      if (adds.length) { await api('/cart/add.js', { items: adds }); cart = await getCart(); }
    }
    return cart;
  };

  let q = Promise.resolve();
  const run = (task) => (q = q.then(task, task));

  const counts = (cart) => {
    HC.cart.state = cart;
    const n = cart.items.filter(i => !HC.isGift(i)).reduce((a, i) => a + i.quantity, 0);
    $$('[data-cart-count]').forEach(el => { el.textContent = n; el.dataset.count = n; });
  };
  const render = async (cart) => {
    counts(cart);
    const ids = [...new Set($$('[data-cart-section]').map(el => el.dataset.cartSection))];
    if (!ids.length) return;
    try {
      const html = await api(`/?sections=${ids.join(',')}`);
      $$('[data-cart-section]').forEach(el => {
        const src = html[el.dataset.cartSection]; if (!src) return;
        const fresh = new DOMParser().parseFromString(src, 'text/html').querySelector(`[data-cart-section="${el.dataset.cartSection}"]`);
        if (fresh) el.innerHTML = fresh.innerHTML;
      });
    } catch (e) {}
  };

  HC.cart = {
    state: null,
    mutate(fn, { open = false } = {}) {
      return run(async () => {
        await fn();
        const cart = await reconcile();
        await render(cart);
        if (open) openDrawer('cart');
        return cart;
      });
    },
    add(items, opts) { return this.mutate(() => api('/cart/add.js', { items }), opts); },
    /* A decrease must shrink the free bottles in the same request: the checkout guard rejects any state
       with more gift bottles than concentrates, so the concentrate line and its gifts go through one /cart/update.js. */
    change(key, quantity) {
      return this.mutate(async () => {
        const cart = await getCart();
        const line = cart.items.find(i => i.key === key);
        if (!line || HC.isGift(line) || quantity >= line.quantity) return api('/cart/change.js', { id: key, quantity });
        const sim = { items: cart.items.map(i => i.key === key ? { ...i, quantity } : i).filter(i => i.quantity > 0) };
        const { updates } = plan(sim);
        updates[key] = quantity;
        return api('/cart/update.js', { updates });
      });
    },
    refresh({ force = false } = {}) { return run(async () => { const cart = await reconcile(); if (force || changed) await render(cart); else counts(cart); return cart; }); }
  };

  /* Other scripts (apps) writing to the cart: reconcile after them. Only reconciles that had to write are rate-limited,
     so two scripts can never ping-pong each other. */
  const cartWrite = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  /* Bundler re-writes line properties after every cart read; those calls never change quantities, so they are ignored. */
  const ignored = /bundler-cart-call/;
  let extT; let writes = []; let hits = [];
  const external = () => {
    clearTimeout(extT);
    extT = setTimeout(async () => {
      const now = Date.now(); writes = writes.filter(t => now - t < 10000); hits = hits.filter(t => now - t < 10000);
      if (hits.length >= 8) return; hits.push(now);
      if (writes.length >= 4) { const c = await getCart().catch(() => null); c && counts(c); return; }
      await HC.cart.refresh({ force: true });
      if (changed) writes.push(Date.now());
    }, 400);
  };
  window.fetch = function (input, init) {
    const p = nativeFetch(input, init);
    try { const u = typeof input === 'string' ? input : input && input.url; if (!internal && u && cartWrite.test(u) && !ignored.test(u)) p.then(external, () => {}); } catch (e) {}
    return p;
  };
  const xo = XMLHttpRequest.prototype.open, xs = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (m, u) { this._hc = !internal && cartWrite.test(String(u)) && !ignored.test(String(u)); return xo.apply(this, arguments); };
  XMLHttpRequest.prototype.send = function () { if (this._hc) this.addEventListener('loadend', external); return xs.apply(this, arguments); };

  /* ---------- drawers + menu ---------- */
  let lastFocus;
  const openDrawer = (id) => {
    const d = document.getElementById(`drawer-${id}`); if (!d) return;
    lastFocus = document.activeElement;
    d.classList.add('is-open'); d.removeAttribute('inert'); d.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
    setTimeout(() => { const f = d.querySelector('[data-autofocus]') || d.querySelector('button, a, input'); f && f.focus({ preventScroll: true }); }, 80);
  };
  const closeDrawer = (d) => {
    d.classList.remove('is-open'); d.setAttribute('inert', ''); d.setAttribute('aria-hidden', 'true');
    if (!$('.drawer.is-open') && !$('.menu.is-open')) document.documentElement.style.overflow = '';
    lastFocus && lastFocus.focus && lastFocus.focus({ preventScroll: true });
  };
  HC.openDrawer = openDrawer;
  const menu = $('#menu'); const burger = $('[data-burger]');
  const setMenu = (open) => {
    if (!menu) return;
    menu.classList.toggle('is-open', open); burger && burger.setAttribute('aria-expanded', String(open));
    open ? menu.removeAttribute('inert') : menu.setAttribute('inert', '');
    document.documentElement.style.overflow = open ? 'hidden' : '';
  };
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-burger]')) { setMenu(!menu.classList.contains('is-open')); return; }
    const o = e.target.closest('[data-open-drawer]');
    if (o) { e.preventDefault(); setMenu(false); openDrawer(o.dataset.openDrawer); if (o.dataset.openDrawer === 'cart') HC.cart.refresh({ force: true }); return; }
    const c = e.target.closest('[data-close-drawer]');
    if (c) { e.preventDefault(); closeDrawer(c.closest('.drawer')); }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') { const d = $('.drawer.is-open'); if (d) closeDrawer(d); else if (menu && menu.classList.contains('is-open')) { setMenu(false); burger && burger.focus(); } }
    const d = $('.drawer.is-open'); if (!d || e.key !== 'Tab') return;
    const f = $$('a[href], button:not([disabled]), input:not([disabled]), select, textarea', d).filter(x => x.offsetParent !== null);
    if (!f.length) return; const first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  /* ---------- cart lines ---------- */
  document.addEventListener('click', (e) => {
    const s = e.target.closest('[data-step]');
    if (s && s.closest('[data-line]')) {
      const line = s.closest('[data-line]'); const input = $('input', line);
      const q = Math.max(0, (parseInt(input.value, 10) || 0) + Number(s.dataset.step));
      input.value = q; HC.cart.change(line.dataset.line, q).catch(err => HC.toast(err.message)); return;
    }
    const rm = e.target.closest('[data-remove]');
    if (rm) HC.cart.change(rm.closest('[data-line]').dataset.line, 0).catch(err => HC.toast(err.message));
  });
  document.addEventListener('change', (e) => {
    const i = e.target.closest('[data-line] input');
    if (i) HC.cart.change(i.closest('[data-line]').dataset.line, Math.max(0, parseInt(i.value, 10) || 0)).catch(err => HC.toast(err.message));
  });
  document.addEventListener('submit', async (e) => {
    const f = e.target.closest('form[data-checkout]'); if (!f) return;
    e.preventDefault(); const b = $('[type="submit"]', f); b && b.classList.add('is-loading');
    try { await HC.cart.refresh(); } catch (err) {}
    window.location.href = url('/checkout');
  });

  /* kit lines: the chosen scents ride along as line-item properties */
  const props = (root) => { const o = {}; $$('[data-kit-pick]', root).forEach(i => { if (i.type === 'radio' && !i.checked) return; o[i.name.replace(/^properties\[|\]$/g, '')] = i.value; }); return Object.keys(o).length ? o : undefined; };

  /* ---------- buy now: add (with the free bottle) then straight to checkout ---------- */
  document.addEventListener('click', async (e) => {
    const b = e.target.closest('[data-buy-now]'); if (!b) return;
    e.preventDefault();
    const form = b.closest('.pdp-form') || $('.pdp-form'); if (!form) return;
    const id = Number($('input[name="id"]', form).value); const qty = Math.max(1, parseInt($('input[name="quantity"]', form).value, 10) || 1);
    const extra = $$('input[data-addon]:checked', form).map(i => ({ id: Number(i.value), quantity: 1 }));
    b.classList.add('is-loading'); b.disabled = true;
    try { await HC.cart.add([{ id, quantity: qty, properties: props(form) }, ...extra]); location.href = `${HC.routes.root}checkout`.replace('//', '/'); }
    catch (err) { HC.toast(err.status === 422 ? T('soldOutMsg') : T('cartError')); b.classList.remove('is-loading'); b.disabled = false; }
  });

  document.addEventListener('change', (e) => {
    const r = e.target.closest('input[data-kit-pick][data-look]'); if (!r || !r.checked) return;
    const line = r.closest('[data-kit-line]'); const l = JSON.parse(r.dataset.look); if (!line || !l) return;
    line.style.setProperty('--ground', l.ground); line.style.setProperty('--ink', l.ink);
    const img = $('.kit__img img', line); if (img && l.src) { img.classList.add('is-out'); setTimeout(() => { img.src = l.src; img.srcset = l.srcset; img.classList.remove('is-out'); }, 180); }
    const slot = $(`[data-kit-stage] [data-stage-slot="${line.dataset.line}"]`); const si = slot && $('img', slot);
    if (si && l.src) { slot.classList.add('is-swap'); setTimeout(() => { si.src = l.src; si.srcset = l.srcset; slot.style.setProperty('--ground', l.ground); slot.classList.remove('is-swap'); }, 220); }
  });

  /* ---------- quick add ---------- */
  document.addEventListener('click', async (e) => {
    const b = e.target.closest('[data-quick]'); if (!b) return;
    e.preventDefault();
    if (b.dataset.options === 'true') { window.location.href = b.dataset.url; return; }
    b.classList.add('is-loading'); b.disabled = true;
    try { await HC.cart.add([{ id: Number(b.dataset.variant), quantity: 1 }], { open: true }); }
    catch (err) { HC.toast(err.status === 422 ? T('soldOutMsg') : T('cartError')); }
    finally { b.classList.remove('is-loading'); b.disabled = false; }
  });

  /* ---------- product form: scent changes swap the bottle, the ground and the ink ---------- */
  class Product {
    constructor(el) {
      this.el = el;
      this.variants = JSON.parse($('[data-variants]', el).textContent);
      this.looks = JSON.parse(($('[data-looks]', el) || { textContent: '[]' }).textContent);
      this.form = $('form.pdp-form', el);
      this.id = this.form && $('input[name="id"]', this.form);
      this.litres = Number(el.dataset.litres || 0);
      el.addEventListener('change', (e) => { if (e.target.matches('[data-option]')) this.pick(); });
      this.form && this.form.addEventListener('submit', (e) => this.submit(e));
      $$('[data-qstep]', el).forEach(b => b.addEventListener('click', () => { const i = $('input[name="quantity"]', el); i.value = Math.max(1, (parseInt(i.value, 10) || 1) + Number(b.dataset.qstep)); }));
    }
    pick() {
      const sel = $$('fieldset[data-index]', this.el).map(fs => { const c = $('input:checked', fs); return c ? c.value : null; });
      $$('[data-opt-value]', this.el).forEach(n => { n.textContent = sel[Number(n.dataset.optValue)] || ''; });
      const v = this.variants.find(x => x.options.every((o, i) => o === sel[i]));
      if (!v) { this.btn(false, T('unavailable')); return; }
      if (this.id) this.id.value = v.id;
      const u = new URL(location.href); u.searchParams.set('variant', v.id); history.replaceState(history.state, '', u);
      this.btn(v.available, v.available ? T('addToCart') : T('soldOut'));
      $$('[data-price-now]').forEach(n => n.textContent = HC.money(v.price));
      $$('[data-price-was]').forEach(n => { const on = v.compare_at_price > v.price; n.hidden = !on; n.textContent = on ? HC.money(v.compare_at_price) : ''; });
      if (this.litres) $$('[data-price-per]').forEach(n => n.textContent = T('perLitre').replace('{price}', HC.money(Math.round(v.price / this.litres))));
      $$('[data-sticky-variant]').forEach(n => n.textContent = v.title === 'Default Title' ? '' : v.title);
      const look = this.looks.find(l => Number(l.id) === Number(v.id));
      if (look) this.look(look);
    }
    look(l) {
      document.documentElement.style.setProperty('--ground', l.ground);
      $$('[data-ground]').forEach(n => { n.style.setProperty('--ground', l.ground); n.style.setProperty('--ink', l.ink); });
      const root = this.el.closest('.pdp') || document;
      const img = $('[data-hero-btl] img.btl:not(.btl--q)', root); if (!img || !l.src || img.getAttribute('src') === l.src) return;
      const q = $('[data-hero-btl] img.btl--q', root);
      const swap = () => { img.src = l.src; img.srcset = l.srcset; if (q && l.srcq) { q.src = l.srcq; q.srcset = l.srcsetq; } img.classList.remove('is-out'); };
      if (RM) return swap();
      img.classList.add('is-out');
      const pre = new Image(); pre.srcset = l.srcset; pre.sizes = img.sizes; pre.src = l.src;
      (pre.decode ? pre.decode() : Promise.resolve()).catch(() => {}).then(() => setTimeout(swap, 220));
      document.dispatchEvent(new CustomEvent('hc:look', { detail: l }));
    }
    btn(ok, label) { $$('[data-add]').forEach(b => { b.disabled = !ok; const s = $('[data-label]', b); s ? (s.textContent = label) : (b.textContent = label); }); }
    async submit(e) {
      e.preventDefault();
      const qty = Math.max(1, parseInt(($('input[name="quantity"]', this.el) || {}).value, 10) || 1);
      const extra = $$('input[data-addon]:checked', this.el).map(i => ({ id: Number(i.value), quantity: 1 }));
      const err = $('[data-error]', this.el); err && (err.textContent = '');
      $$('[data-add]').forEach(b => b.classList.add('is-loading'));
      try { await HC.cart.add([{ id: Number(this.id.value), quantity: qty, properties: props(this.el) }, ...extra], { open: true }); }
      catch (ex) { err && (err.textContent = ex.status === 422 ? T('soldOutMsg') : (ex.message || T('cartError'))); }
      finally { $$('[data-add]').forEach(b => b.classList.remove('is-loading')); }
    }
  }
  $$('[data-product]').forEach(el => new Product(el));
  document.addEventListener('click', (e) => { if (e.target.closest('[data-sticky-add]')) { e.preventDefault(); const f = $('form.pdp-form'); f && f.requestSubmit(); } });

  /* collection cards: hovering a scent dot previews that scent (image, ground, ink, link) */
  const cardOf = (d) => d.closest('.card');
  const cardImg = (c) => $('.card__media img.btl:not(.btl--photo)', c);
  document.addEventListener('mouseover', (e) => {
    const d = e.target.closest('.swatches i[data-look]'); if (!d || d.classList.contains('is-on')) return;
    const c = cardOf(d), img = c && cardImg(c); if (!c || !img) return;
    let l; try { l = JSON.parse(d.dataset.look); } catch (_) { return; } if (!l.src) return;
    const a = $('.card__t a', c);
    if (!c.dataset.look0) c.dataset.look0 = JSON.stringify({ src: img.src, srcset: img.srcset, ground: c.style.getPropertyValue('--ground'), ink: c.style.getPropertyValue('--ink'), href: a ? a.getAttribute('href') : '' });
    img.src = l.src; img.srcset = l.srcset || ''; c.style.setProperty('--ground', l.ground); c.style.setProperty('--ink', l.ink);
    if (a && d.dataset.url) a.setAttribute('href', d.dataset.url);
    $$('.swatches i', c).forEach(x => x.classList.toggle('is-on', x === d));
  });
  document.addEventListener('mouseout', (e) => {
    const sw = e.target.closest('.swatches'); if (!sw || sw.contains(e.relatedTarget)) return;
    const c = cardOf(sw), img = c && cardImg(c); if (!c || !img || !c.dataset.look0) return;
    const o = JSON.parse(c.dataset.look0); delete c.dataset.look0;
    img.src = o.src; img.srcset = o.srcset; c.style.setProperty('--ground', o.ground); c.style.setProperty('--ink', o.ink);
    const a = $('.card__t a', c); if (a && o.href) a.setAttribute('href', o.href);
    $$('.swatches i', c).forEach(x => x.classList.remove('is-on'));
  });

  /* gallery thumbs on the product stage (photos beyond the render) */
  $$('[data-thumb]').forEach(t => t.addEventListener('click', () => {
    const hero = $('[data-hero-btl]'), stage = $('[data-pdp-stage]'); if (!hero || !stage) return;
    $$('[data-thumb]').forEach(x => x.setAttribute('aria-current', String(x === t)));
    if (t.dataset.photo === 'true') {                                   // a scene photo fills the stage
      let ph = $('.pdp__photo', stage); if (!ph) { ph = document.createElement('img'); ph.className = 'pdp__photo'; ph.alt = ''; ph.decoding = 'async'; stage.appendChild(ph); }
      ph.srcset = t.dataset.srcset || ''; ph.sizes = '(min-width: 990px) 54vw, 100vw'; ph.src = t.dataset.src; stage.classList.add('has-photo'); return;
    }
    stage.classList.remove('has-photo');
    if (hero.classList.contains('pdp__btl--kit') || !t.dataset.src) return;        // kits keep their composed stage
    const img = $('img', hero); if (!img) return;
    img.classList.add('is-out'); setTimeout(() => { img.src = t.dataset.src; img.srcset = t.dataset.srcset || ''; img.classList.remove('is-out'); }, RM ? 0 : 220);
  }));

  /* the scene photo follows the scent */
  document.addEventListener('hc:look', (e) => {
    const l = e.detail || {};
    // the render thumbs (front, three-quarter) follow the chosen scent too
    const tf = $('[data-thumb][data-kind="front"]'), tq = $('[data-thumb][data-kind="q"]');
    if (tf && l.src) { tf.dataset.src = l.src; tf.dataset.srcset = l.srcset || ''; const i = $('img', tf); if (i) i.src = l.src; }
    if (tq && l.srcq) { tq.dataset.src = l.srcq; tq.dataset.srcset = `${l.srcq} 840w`; const i = $('img', tq); if (i) i.src = l.srcq; }
    const k = l.key; const t = $('[data-thumb][data-vkey]'); if (!k || !t || t.dataset.vkey === k) return;
    const b = t.dataset.base, s7 = `${b}gen-v-${k}-700.webp`, s14 = `${b}gen-v-${k}.webp`;
    t.dataset.vkey = k; t.dataset.src = s14; t.dataset.srcset = `${s7} 700w, ${s14} 1400w`; const ti = $('img', t); if (ti) ti.src = s7;
    const stage = $('[data-pdp-stage]'), ph = stage && $('.pdp__photo', stage);
    if (ph && stage.classList.contains('has-photo') && t.getAttribute('aria-current') === 'true') {
      const pre = new Image(); pre.src = s14; const swap = () => { ph.srcset = t.dataset.srcset; ph.src = s14; ph.classList.remove('is-out'); };
      if (RM) return swap(); ph.classList.add('is-out'); (pre.decode ? pre.decode() : Promise.resolve()).catch(() => {}).then(() => setTimeout(swap, 200));
    }
  });

  /* sticky buy bar */
  const anchor = $('[data-sticky-anchor]'); const bar = $('[data-sticky]');
  if (anchor && bar) new IntersectionObserver(([en]) => { const on = !en.isIntersecting && en.boundingClientRect.top < 0; bar.classList.toggle('is-on', on); document.body.classList.toggle('has-sticky', on); }).observe(anchor);

  /* collection filters (client side, by category) */
  $$('[data-filters]').forEach(bar => bar.addEventListener('click', (e) => {
    const b = e.target.closest('button[data-f]'); if (!b) return;
    $$('button', bar).forEach(x => x.setAttribute('aria-pressed', String(x === b)));
    const f = b.dataset.f; $$('[data-cat]').forEach(c => { c.hidden = f !== '*' && !c.dataset.cat.split(' ').includes(f); });
  }));

  /* coming-soon countdown */
  $$('[data-countdown]').forEach(el => {
    const end = new Date(el.dataset.countdown).getTime();
    const tick = () => { const s = Math.max(0, Math.floor((end - Date.now()) / 1000));
      const v = { d: Math.floor(s / 86400), h: Math.floor(s / 3600) % 24, m: Math.floor(s / 60) % 60, s: s % 60 };
      Object.entries(v).forEach(([k, n]) => { const t = $(`[data-cd="${k}"]`, el); if (t) t.textContent = String(n).padStart(2, '0'); }); };
    tick(); if (!RM) setInterval(tick, 1000); else setInterval(tick, 60000);
  });

  /* reveals + yield dots */
  if (!RM && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver(es => es.forEach(en => { if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); } }), { rootMargin: '0px 0px -10% 0px' });
    $$('[data-reveal], .yieldx').forEach(el => io.observe(el));
  } else $$('[data-reveal], .yieldx').forEach(el => el.classList.add('is-in'));

  /* boot: reconcile on load, back/forward cache, and when the tab comes back */
  const boot = () => HC.cart.refresh().catch(() => {});
  boot();
  addEventListener('pageshow', (e) => { if (e.persisted) boot(); });
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') boot(); });
  window.HC = HC;
})();
